import { Roles } from 'src/app/Shared/Interfaces/roles.interface';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { AuthenticatorService, UserType } from './authenticator.service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import moment, { duration } from 'moment';
import { EncryptorService } from './encryptor.service';
import { resolve } from 'dns';

export interface Ping {
    success?: boolean;
    score?: number;
    greeting: string;
    date: string;
    url: string;
    headers: Object;
}

export interface Path {
    path: string;
}
@Injectable({
    providedIn: 'root',
})
export class PingService {
    baseUrl: string = environment.baseUrl;

    constructor(
        private http: HttpClient,
        private auth: AuthenticatorService,
        private recaptchaV3Service: ReCaptchaV3Service,
        private encryptorService: EncryptorService,
        private authService: AuthenticatorService
    ) {}

    get token_captcha2(): string {
        return localStorage.getItem('TOKEN_R2');
    }

    set token_captcha2(token_captcha2: string) {
        localStorage.setItem('TOKEN_R2', token_captcha2);
    }

    get omit_recapcha(): string {
        return localStorage.getItem('OMO_RECAP') ?? '';
    }

    set omit_recapcha(token_captcha2: string) {
        localStorage.setItem('OMO_RECAP', token_captcha2);
    }

    get force_recapcha(): boolean {
        const invalid = sessionStorage.getItem('FCE_RECAP');
        const decrypted: string = invalid
            ? this.encryptorService.aesDecrypt(invalid)
            : false;
        return JSON.parse(decrypted);
    }

    set force_recapcha(force: boolean) {
        const encrypted: string = this.encryptorService.aesEncrypt(
            (force ?? false).toString()
        );
        localStorage.setItem('FCE_RECAP', encrypted);
    }

    get score(): number {
        const invalid = sessionStorage.getItem('SCR');
        const decrypted: string = invalid
            ? this.encryptorService.aesDecrypt(invalid)
            : 10;
        return JSON.parse(decrypted);
    }

    set score(score: number) {
        const encrypted: string = this.encryptorService.aesEncrypt(
            (score ?? 0).toString()
        );
        localStorage.setItem('SCR', encrypted);
    }

    ping(): Observable<Ping> {
        const headers = this.auth.headers;
        const url_api = `${this.baseUrl}/ping`;
        return this.http.get<Ping>(url_api, { headers });
    }

    makePing(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.ping().subscribe(
                (ping) => {
                    resolve(true);
                },
                (error) => resolve(false)
            );
        });
    }

    path(path: string): Observable<Ping> {
        const headers = this.auth.headers;
        const url_api = `${this.baseUrl}/users/path`;
        return this.http.post<Ping>(url_api, { path }, { headers });
    }

    savePath(path: string): Promise<void> {
        return new Promise<void>((resolve) => {
            this.path(path).subscribe(
                (ping) => {
                    resolve();
                },
                (error) => resolve()
            );
        });
    }

    pingRecapchaV3(token: string): Observable<Ping> {
        const headers = this.auth.headers;
        const url_api = `${this.baseUrl}/validate-recaptcha-v3`;
        return this.http.post<Ping>(url_api, { token }, { headers });
    }

    pingRecapchaV2(token: string): Observable<Ping> {
        const headers = this.auth.headers;
        const url_api = `${this.baseUrl}/validate-recaptcha-v2`;
        return this.http.post<Ping>(url_api, { token }, { headers });
    }

    validateRecapchaV3(token: string): Promise<boolean> {
        return new Promise<boolean>(async (resolve) => {
            const omitRecapcha = this.omitRecapcha();
            if (!omitRecapcha && this.force_recapcha) {
                const token_captcha2 = this.token_captcha2;

                if (token_captcha2) {
                    const valid = await this.validateRecapchaV2(token_captcha2);

                    localStorage.removeItem('TOKEN_R2');

                    this.force_recapcha = !valid;
                    resolve(valid);
                } else {
                    this.force_recapcha = true;
                    resolve(false);
                }
            } else if (!omitRecapcha) {
                this.pingRecapchaV3(token).subscribe(
                    async (ping) => {
                        const valid: boolean = ping.success && ping.score > 0.5;
                        this.score = ping.score;

                        if (valid) {
                            this.force_recapcha = !valid;
                            resolve(valid);
                        } else {
                            const token_captcha2 = this.token_captcha2;

                            if (token_captcha2) {
                                const valid2 = await this.validateRecapchaV2(
                                    token_captcha2
                                );

                                localStorage.removeItem('TOKEN_R2');

                                this.force_recapcha = !valid2;
                                resolve(valid2);
                            } else {
                                this.force_recapcha = true;
                                resolve(false);
                            }
                        }
                    },
                    (error) => resolve(false)
                );
            } else {
                this.force_recapcha = false;
                resolve(true);
            }
        });
    }

    validateRecapchaV2(token: string): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.pingRecapchaV2(token).subscribe(
                (ping) => {
                    this.omit_recapcha = this.encrypt(ping.date);
                    this.score = 0;
                    resolve(ping.success);
                },
                (error) => resolve(false)
            );
        });
    }

    async recaptchaV3(action: string): Promise<string | undefined> {
        return new Promise<string | undefined>((resolve) => {
            this.recaptchaV3Service.execute(action).subscribe(
                (token: string) => {
                    resolve(token);
                },
                (error) => {
                    resolve(undefined);
                }
            );
        });
    }

    encrypt(date: string): string {
        const duration = this.auth.expectedRole(UserType.CoordinacionCarreras)
            ? 300
            : this.auth.expectedRole(UserType.ServiciosEscolares)
            ? 200
            : !this.auth.expectedRole(UserType.Alumno)
            ? 100
            : 50;
        return this.encryptorService.aesEncrypt(
            JSON.stringify({
                exp: moment(date).add(this.score * duration, 'minutes'),
            })
        );
    }

    decrypt(date: string): string {
        return this.encryptorService.aesDecrypt(date);
    }

    omitRecapcha(): boolean {
        const omit_recapcha = this.omit_recapcha;
        if (omit_recapcha != '') {
            try {
                const data = JSON.parse(
                    this.decrypt(this.omit_recapcha) ?? '{}'
                );
                const now = new Date();
                const limit = new Date(data.exp);
                return limit >= now;
            } catch (error) {
                return false;
            }
        } else {
            return false;
        }
    }
}
